import React, { useEffect, useRef, useState } from 'react';
import '../styles/ScrollingText.css';
import WorldTime from './WorldTime';
import Weather from './Weather';

const ScrollingText = ({ content, speed, backgroundColor, textColor, scale = 1, fontFamily = 'Roboto', weatherApiKey = '' }) => {
  const tickerRef = useRef(null);
  const primaryRef = useRef(null);
  const secondaryRef = useRef(null);
  const [renderedContent, setRenderedContent] = useState('');
  // Add a debug mode state (for development only)
  const [debugMode] = useState(false);

  // Load Google Font
  useEffect(() => {
    // Skip if using a system font
    if (['Arial', 'Helvetica', 'Verdana', 'Times New Roman', 'Courier New', 'serif', 'sans-serif'].includes(fontFamily)) {
      return;
    }

    // Create a link element for the Google Font
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}:wght@400;700&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      // Clean up when component unmounts or font changes
      document.head.removeChild(link);
    };
  }, [fontFamily]);

  // Parse and format the content with special formatting
  const parseContent = (text) => {
    if (!text) return '';

    console.log("Original content:", text);
    
    // Replace |space| with a space character
    text = text.replace(/\|space\|/g, ' ');

    // Extract time widgets for separate handling
    const timeWidgets = [];
    // Use a unique ID to replace each time widget in the text
    text = text.replace(/\|time\[([\w\/]+)(?:,([\w\s()]+))?(?:,(12h|24h))?\]\|/g, (match, timeZone, label = '', format = '12h') => {
      const id = `TIME_WIDGET_${timeWidgets.length}`;
      timeWidgets.push({ timeZone, label, format, id });
      return id; // Replace with a placeholder ID that we'll restore later
    });

    // Extract weather widgets for separate handling
    const weatherWidgets = [];
    // Use a unique ID to replace each weather widget in the text
    text = text.replace(/\|weather\[([^\]]+)\]\|/g, (match, paramsStr) => {
      // Split by comma, but handle spaces around parameters
      const params = paramsStr.split(',').map(p => p.trim());
      
      // Extract parameters with defaults
      const location = params[0] || '';
      const format = params[1] || 'compact';
      const units = params[2] || 'c';
      
      // Create widget ID
      const id = `WEATHER_WIDGET_${weatherWidgets.length}`;
      
      // Store widget data
      weatherWidgets.push({ location, format, units, id });
      
      console.log(`Weather widget parsed: location=${location}, format=${format}, units=${units}`);
      
      // Replace with widget ID placeholder
      return id;
    });

    // STEP 1: Tokenize the text into literals (words, spaces, formatting tags, etc.)
    function tokenize(input) {
      const tokens = [];
      let currentPosition = 0;
      
      // Define all the formatting patterns
      const patterns = [
        // Patterns that should be handled as a unit
        { type: 'divider', regex: /\|div\|/g },
        { type: 'image', regex: /!img\[https?:\/\/[^\s]+\]/g },
        
        // Time widget placeholders are now handled separately
        { type: 'time_placeholder', regex: /TIME_WIDGET_\d+/g },
        
        // Weather widget placeholders
        { type: 'weather_placeholder', regex: /WEATHER_WIDGET_\d+/g },
        
        // Emoji patterns
        { type: 'emoji', regex: /:smile:/g, replacement: '😊' },
        { type: 'emoji', regex: /:heart:/g, replacement: '❤️' },
        { type: 'emoji', regex: /:star:/g, replacement: '⭐' },
        { type: 'emoji', regex: /:warning:/g, replacement: '⚠️' },
        { type: 'emoji', regex: /:fire:/g, replacement: '🔥' },
        { type: 'emoji', regex: /:thumbsup:/g, replacement: '👍' },
        { type: 'emoji', regex: /:clock:/g, replacement: '🕒' },
        { type: 'emoji', regex: /:news:/g, replacement: '📰' },
        { type: 'emoji', regex: /:mic:/g, replacement: '🎤' },
        { type: 'emoji', regex: /:tv:/g, replacement: '📺' },
        
        // Formatting patterns
        { type: 'bold', regex: /\*[^*]+\*/g },
        { type: 'italic', regex: /_[^_]+_/g },
        { type: 'blink', regex: /~[^~]+~/g },
        { type: 'color', regex: /#[a-zA-Z0-9#]+\[[^\]]+\]/g },
        { type: 'size', regex: /\^(small|medium|large)\[[^\]]+\]/g },
        { type: 'highlight', regex: /\|h\[([^\]]+)\]\|/g },
        { type: 'pulse', regex: /\|pulse\[[^\]]+\]\|/g },
        
        // Space and word patterns (must be last)
        { type: 'space', regex: /\s+/g },
        { type: 'word', regex: /[^\s]+/g },
      ];
      
      // Find all tokens
      while (currentPosition < input.length) {
        let matched = false;
        
        // Try each pattern
        for (const pattern of patterns) {
          pattern.regex.lastIndex = currentPosition;
          const match = pattern.regex.exec(input);
          
          // If we found a match at the current position
          if (match && match.index === currentPosition) {
            tokens.push({
              type: pattern.type,
              content: match[0],
              replacement: pattern.replacement
            });
            
            currentPosition = pattern.regex.lastIndex;
            matched = true;
            break;
          }
        }
        
        // If no pattern matched, move to the next character
        if (!matched) {
          currentPosition++;
        }
      }
      
      return tokens;
    }
    
    // STEP 2: Process each token and convert to HTML
    function processTokens(tokens) {
      return tokens.map(token => {
        switch (token.type) {
          case 'space':
            return '&nbsp;';
            
          case 'word':
            return token.content;
            
          case 'divider':
            return '<span class="ticker-divider"></span>';
            
          case 'emoji':
            return token.replacement;
            
          case 'image':
            const imageUrl = token.content.match(/!img\[(https?:\/\/[^\s]+)\]/)[1];
            return `<img src="${imageUrl}" alt="User Image" class="ticker-image" />`;
            
          case 'bold':
            const boldContent = token.content.substring(1, token.content.length - 1);
            return `<strong>${processTokenizedContent(boldContent)}</strong>`;
            
          case 'italic':
            const italicContent = token.content.substring(1, token.content.length - 1);
            return `<em>${processTokenizedContent(italicContent)}</em>`;
            
          case 'blink':
            const blinkContent = token.content.substring(1, token.content.length - 1);
            return `<span class="blink">${processTokenizedContent(blinkContent)}</span>`;
            
          case 'color':
            const colorMatch = token.content.match(/#([a-zA-Z0-9#]+)\[([^\]]+)\]/);
            if (!colorMatch) return token.content;
            return `<span class="color-text" data-color="${colorMatch[1]}">${processTokenizedContent(colorMatch[2])}</span>`;
            
          case 'size':
            const sizeMatch = token.content.match(/\^(small|medium|large)\[([^\]]+)\]/);
            if (!sizeMatch) return token.content;
            return `<span class="text-${sizeMatch[1]}">${processTokenizedContent(sizeMatch[2])}</span>`;
            
          case 'highlight':
            const highlightContent = token.content.match(/\|h\[([^\]]+)\]\|/)[1];
            return `<span class="ticker-highlight">${processTokenizedContent(highlightContent)}</span>`;
            
          case 'pulse':
            const pulseContent = token.content.match(/\|pulse\[([^\]]+)\]\|/)[1];
            return `<span class="pulse">${processTokenizedContent(pulseContent)}</span>`;
            
          case 'time_placeholder':
            // Lookup the time widget info from our extracted collection
            const widgetIndex = parseInt(token.content.replace('TIME_WIDGET_', ''), 10);
            const widget = timeWidgets[widgetIndex];
            if (!widget) return token.content;
            
            // Determine the region class based on the time zone
            let regionClass = '';
            if (widget.timeZone.startsWith('America')) regionClass = 'timezone-us';
            else if (widget.timeZone.startsWith('Europe')) regionClass = 'timezone-eu';
            else if (widget.timeZone.startsWith('Asia') || widget.timeZone.startsWith('Japan')) regionClass = 'timezone-asia';
            else if (widget.timeZone.startsWith('Australia') || widget.timeZone.startsWith('Pacific')) regionClass = 'timezone-aus';
            else if (widget.timeZone === 'UTC' || widget.timeZone.startsWith('GMT')) regionClass = 'timezone-utc';
            
            return `<span class="world-time-placeholder" 
                          data-timezone="${widget.timeZone}" 
                          data-label="${widget.label}" 
                          data-format="${widget.format}"
                          data-region="${regionClass}"
                          data-widget-id="${widget.id}"></span>`;
            
          case 'weather_placeholder':
            // Lookup the weather widget info from our extracted collection
            const weatherIndex = parseInt(token.content.replace('WEATHER_WIDGET_', ''), 10);
            const weatherWidget = weatherWidgets[weatherIndex];
            if (!weatherWidget) return token.content;
            
            // Ensure the format is properly normalized (lowercase and valid)
            let format = (weatherWidget.format || 'compact').toLowerCase();
            // Only accept valid formats
            if (!['compact', 'full', 'icon'].includes(format)) {
              console.log(`Invalid weather format "${format}" defaulting to "compact"`);
              format = 'compact';
            }
            
            return `<span class="weather-placeholder" 
                          data-location="${weatherWidget.location}" 
                          data-format="${format}" 
                          data-units="${weatherWidget.units}"
                          data-widget-id="${weatherWidget.id}"></span>`;
            
          default:
            return token.content;
        }
      });
    }
    
    // STEP 3: Process nested formatting in token content
    function processTokenizedContent(content) {
      // Recursively tokenize and process the content
      const tokens = tokenize(content);
      const processedParts = processTokens(tokens);
      return processedParts.join('');
    }
    
    // Main processing pipeline
    const tokens = tokenize(text);
    const processedParts = processTokens(tokens);
    const result = processedParts.join('');
    
    console.log("Final formatted text:", result);
    return result;
  };

  // Process the content whenever it changes
  useEffect(() => {
    const processedContent = parseContent(content);
    setRenderedContent(processedContent);
  }, [content]);

  // Function to map WeatherAPI condition codes to @bybas/weather-icons
  const mapWeatherConditionToIcon = (code, isDay) => {
    // Default time of day suffix
    const timeOfDay = isDay ? 'day' : 'night';
    
    // Map condition codes to appropriate icons
    switch (code) {
      // Sunny / Clear
      case 1000:
        return isDay ? 'clear-day' : 'clear-night';
        
      // Partly cloudy
      case 1003:
        return `partly-cloudy-${timeOfDay}`;
        
      // Cloudy
      case 1006:
        return 'cloudy';
        
      // Overcast
      case 1009:
        return 'cloudy';
        
      // Mist
      case 1030:
        return 'fog';
        
      // Patchy rain possible
      case 1063:
        return `partly-cloudy-${timeOfDay}-rain`;
        
      // Patchy snow possible
      case 1066:
        return `partly-cloudy-${timeOfDay}-snow`;
        
      // Patchy sleet possible
      case 1069:
        return `partly-cloudy-${timeOfDay}-sleet`;
        
      // Patchy freezing drizzle possible
      case 1072:
        return `partly-cloudy-${timeOfDay}-sleet`;
        
      // Thundery outbreaks possible
      case 1087:
        return `thunderstorms-${timeOfDay}`;
        
      // Blowing snow
      case 1114:
        return 'wind-snow';
        
      // Blizzard
      case 1117:
        return 'wind-snow';
        
      // Fog
      case 1135:
        return 'fog';
        
      // Freezing fog
      case 1147:
        return 'fog';
        
      // Patchy light drizzle / Light drizzle
      case 1150:
      case 1153:
        return 'drizzle';
        
      // Freezing drizzle / Heavy freezing drizzle
      case 1168:
      case 1171:
        return 'sleet';
        
      // Patchy light rain / Light rain
      case 1180:
      case 1183:
        return `partly-cloudy-${timeOfDay}-rain`;
        
      // Moderate rain at times / Moderate rain
      case 1186:
      case 1189:
        return 'rain';
        
      // Heavy rain at times / Heavy rain
      case 1192:
      case 1195:
        return 'rain';
        
      // Light freezing rain / Moderate or heavy freezing rain
      case 1198:
      case 1201:
        return 'sleet';
        
      // Light sleet / Moderate or heavy sleet
      case 1204:
      case 1207:
        return 'sleet';
        
      // Patchy light snow / Light snow
      case 1210:
      case 1213:
        return `partly-cloudy-${timeOfDay}-snow`;
        
      // Patchy moderate snow / Moderate snow
      case 1216:
      case 1219:
        return 'snow';
        
      // Patchy heavy snow / Heavy snow
      case 1222:
      case 1225:
        return 'snow';
        
      // Ice pellets
      case 1237:
        return 'hail';
        
      // Light rain shower
      case 1240:
        return `partly-cloudy-${timeOfDay}-rain`;
        
      // Moderate or heavy rain shower
      case 1243:
        return 'rain';
        
      // Torrential rain shower
      case 1246:
        return 'rain';
        
      // Light sleet showers / Moderate or heavy sleet showers
      case 1249:
      case 1252:
        return 'sleet';
        
      // Light snow showers / Moderate or heavy snow showers
      case 1255:
      case 1258:
        return `partly-cloudy-${timeOfDay}-snow`;
        
      // Light showers of ice pellets / Moderate or heavy showers of ice pellets
      case 1261:
      case 1264:
        return 'hail';
        
      // Patchy light rain with thunder / Moderate or heavy rain with thunder
      case 1273:
      case 1276:
        return `thunderstorms-${timeOfDay}-rain`;
        
      // Patchy light snow with thunder / Moderate or heavy snow with thunder
      case 1279:
      case 1282:
        return `thunderstorms-${timeOfDay}-snow`;
        
      // Default case
      default:
        return isDay ? 'clear-day' : 'clear-night';
    }
  };
  
  // Function to update the weather element with data
  const updateWeatherElement = (element, data, units, format) => {
    console.log(`Updating weather element with format: ${format}, units: ${units}`);
    
    // Clear any existing content
    element.innerHTML = '';
    
    // Get temperature in the requested units
    const temp = units.toLowerCase() === 'f' 
      ? data.current.temp_f 
      : data.current.temp_c;
    
    const tempUnit = units.toLowerCase() === 'f' ? '°F' : '°C';
    const { condition } = data.current;
    
    // Create a better structured layout with proper containment
    element.className = 'weather-widget';
    
    // Map the WeatherAPI condition code to the appropriate @bybas/weather-icons icon
    const iconName = mapWeatherConditionToIcon(condition.code, data.current.is_day);
    const iconPath = `/weather-icons/fill/svg/${iconName}.svg`;
    
    // Get the scale from CSS variable for responsive text trimming
    const scale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--content-scale') || '1');
    
    // Improved dynamic text trimming based on scale
    // Adjust the maximum text length more aggressively for small scales and more generously for large scales
    const maxLocationLength = Math.max(6, Math.min(18, Math.floor(14 * scale)));
    const maxConditionLength = Math.max(5, Math.min(16, Math.floor(12 * scale)));
    
    console.log(`Scale: ${scale}, Max location length: ${maxLocationLength}, Max condition length: ${maxConditionLength}`);
    
    // Trim text if needed with ellipsis
    const locationName = data.location.name.length > maxLocationLength
      ? data.location.name.substring(0, maxLocationLength) + '…' 
      : data.location.name;
      
    const conditionText = condition.text.length > maxConditionLength
      ? condition.text.substring(0, maxConditionLength) + '…'
      : condition.text;
    
    // Format temperature display with no decimal places
    const tempDisplay = `${Math.round(temp)}${tempUnit}`;
    
    // Create a completely new structure with better containment
    element.innerHTML = `
      <div class="weather-widget-inner">
        <div class="weather-left-section">
          <img src="${iconPath}" alt="${condition.text}" class="weather-icon" />
        </div>
        <div class="weather-content-section">
          <div class="weather-info">
            <div class="weather-location" title="${data.location.name}">${locationName}</div>
            <div class="weather-condition" title="${condition.text}">${conditionText}</div>
          </div>
          <div class="weather-temp">${tempDisplay}</div>
        </div>
      </div>
    `;
    
    console.log('Weather widget created with new structure:', element.innerHTML);
  };
  
  // Function to fetch and update weather data
  const fetchWeatherData = async (element, location, units, format) => {
    try {
      console.log(`Fetching weather for: ${location}, format: ${format}, units: ${units}`);
      
      // Show loading state with a proper styled container
      element.className = 'weather-widget loading';
      element.innerHTML = `
        <div class="weather-widget-inner">
          <div class="weather-left-section">
            <div class="weather-loading-spinner"></div>
          </div>
          <div class="weather-content-section">
            <div class="weather-info">
              <div class="weather-location">${location}</div>
              <div class="weather-condition">Loading...</div>
            </div>
          </div>
        </div>
      `;
      
      const response = await fetch(
        `https://api.weatherapi.com/v1/current.json?key=${weatherApiKey}&q=${encodeURIComponent(location)}&aqi=no`
      );
      
      if (!response.ok) {
        throw new Error(`Weather API error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`Weather data received for ${location}:`, data);
      
      // Update the element with weather data
      updateWeatherElement(element, data, units, format);
    } catch (err) {
      console.error(`Error fetching weather for ${location}:`, err);
      
      // Show error state with more informative message
      element.className = 'weather-widget error';
      element.innerHTML = `
        <div class="weather-widget-inner">
          <div class="weather-left-section">
            <div class="weather-error-icon">!</div>
          </div>
          <div class="weather-content-section">
            <div class="weather-info">
              <div class="weather-location">${location}</div>
              <div class="weather-condition">Weather unavailable</div>
            </div>
          </div>
        </div>
      `;
    }
  };

  // Set up the seamless scrolling animation
  useEffect(() => {
    if (!tickerRef.current || !primaryRef.current || !secondaryRef.current) return;
    
    const ticker = tickerRef.current;
    const primary = primaryRef.current;
    const secondary = secondaryRef.current;
    
    // Calculate the animation parameters
    const setupAnimation = () => {
      // Get dimensions
      const containerWidth = ticker.parentElement.offsetWidth;
      const contentWidth = primary.offsetWidth;
      
      // If content is empty or too small, don't animate
      if (contentWidth < 10) return;
      
      console.log(`Container width: ${containerWidth}px, Content width: ${contentWidth}px`);
      
      // Ensure the CSS scale variable is properly set
      document.documentElement.style.setProperty('--content-scale', scale.toString());
      
      // Determine how many duplicates we need
      // We need at least enough copies to fill the screen twice plus one extra for smoother transitions
      const copiesNeeded = Math.max(3, Math.ceil((containerWidth * 3) / contentWidth));
      
      console.log(`Copies needed: ${copiesNeeded}`);
      
      // Create the necessary number of copies
      // First clear any existing duplicates (keeping just primary and secondary)
      while (ticker.children.length > 2) {
        const childToRemove = ticker.lastChild;
        
        // Clean up any intervals for time widgets
        const timeWidgets = childToRemove.querySelectorAll('.world-time-value');
        timeWidgets.forEach(widget => {
          const intervalId = widget.getAttribute('data-interval-id');
          if (intervalId) {
            clearInterval(parseInt(intervalId, 10));
          }
        });
        
        ticker.removeChild(childToRemove);
      }
      
      // Add additional copies as needed
      for (let i = 2; i < copiesNeeded; i++) {
        const clone = primary.cloneNode(true);
        clone.setAttribute('data-clone-index', i);
        ticker.appendChild(clone);
        
        // Initialize any widgets in the clone
        initializeWidgets(clone);
      }
      
      // Get all ticker items after they've been added
      const tickerItems = ticker.children;
      const totalItems = tickerItems.length;
      
      // Reset all individual transforms first
      Array.from(tickerItems).forEach(item => {
        item.style.transform = 'none';
      });
      
      // Calculate the proper animation parameters:
      // For perfectly seamless scrolling, the animation should move exactly one content width
      // The key is to restart the animation before the user can see the "restart"
      let animationDistance = contentWidth;
      
      // Base duration in seconds (adjusted by speed)
      const baseDuration = 20;
      
      // Speed factor - map from 1-10 to slower-faster
      const speedFactor = (11 - speed) / 2.5; // Convert 1-10 to 4-0.4
      
      // Adjust duration based on content width and speed
      const widthFactor = Math.min(2, Math.max(0.5, contentWidth / (containerWidth * 0.8)));
      const duration = baseDuration * speedFactor * widthFactor;
      
      console.log(`Animation setup: Items=${totalItems}, Content width=${contentWidth}px, Animation distance=${animationDistance}px, Duration=${duration}s`);
      
      // Completely reset the ticker element
      ticker.style.animation = 'none';
      ticker.style.transform = 'translateX(0)';
      
      // Force a reflow to ensure the reset is applied
      ticker.offsetHeight;
      
      // Set the CSS variable for animation distance
      document.documentElement.style.setProperty('--animation-distance', `${animationDistance}px`);
      
      // Apply the animation with hardware acceleration
      ticker.style.animation = `ticker-scroll ${duration}s linear infinite`;
      ticker.style.backfaceVisibility = 'hidden';
      ticker.style.willChange = 'transform';
      
      // In debug mode, add visible boundaries to ticker items
      if (debugMode) {
        Array.from(tickerItems).forEach((child, index) => {
          child.style.backgroundColor = index % 2 === 0 ? 'rgba(255,0,0,0.1)' : 'rgba(0,0,255,0.1)';
          child.style.border = '1px dashed rgba(255,255,255,0.5)';
        });
        
        console.log('Debug mode: Ticker clones:', tickerItems.length);
      }
    };

    // Setup initial animation with a delay to ensure DOM is ready
    setTimeout(setupAnimation, 100);
    
    // Adjust animation when window is resized
    const handleResize = () => {
      setupAnimation();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [content, speed, debugMode, scale]);

  // Function to update time value
  const updateTimeValue = (element, timeZone, format) => {
    const updateTime = () => {
      try {
        const options = { timeZone, hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: format === '12h' };
        const formatter = new Intl.DateTimeFormat('en-US', options);
        element.textContent = formatter.format(new Date());
      } catch (error) {
        console.error(`Error formatting time for ${timeZone}:`, error);
        element.textContent = 'Invalid timezone';
      }
    };
    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    element.setAttribute('data-interval-id', intervalId);
  };

  // Add a helper function to initialize widgets in cloned content
  const initializeWidgets = (container) => {
    if (!container) return;
    
    // Process time placeholders
    const timePlaceholders = container.querySelectorAll('.world-time-placeholder');
    timePlaceholders.forEach(placeholder => {
      const timeZone = placeholder.getAttribute('data-timezone');
      const label = placeholder.getAttribute('data-label');
      const format = placeholder.getAttribute('data-format');
      const regionClass = placeholder.getAttribute('data-region');

      const timeWidget = document.createElement('span');
      timeWidget.className = `world-time ${regionClass}`;

      if (label) {
        const labelElem = document.createElement('span');
        labelElem.className = 'world-time-label';
        labelElem.textContent = `${label}: `;
        timeWidget.appendChild(labelElem);
      }

      const timeValueElem = document.createElement('span');
      timeValueElem.className = 'world-time-value';
      timeValueElem.setAttribute('data-timezone', timeZone);
      timeValueElem.setAttribute('data-format', format);
      timeWidget.appendChild(timeValueElem);

      placeholder.parentNode.replaceChild(timeWidget, placeholder);
      updateTimeValue(timeValueElem, timeZone, format);
    });

    // Process weather placeholders
    const weatherPlaceholders = container.querySelectorAll('.weather-placeholder');
    weatherPlaceholders.forEach(placeholder => {
      const location = placeholder.getAttribute('data-location');
      const format = placeholder.getAttribute('data-format');
      const units = placeholder.getAttribute('data-units');
      
      // Create a simple weather widget (we can't use React components directly here)
      if (weatherApiKey) {
        const weatherWidget = document.createElement('span');
        weatherWidget.className = `weather-widget ${format}`;
        weatherWidget.setAttribute('data-loading', 'true');
        
        // Initially show loading state
        weatherWidget.textContent = 'Loading weather...';
        
        // Replace the placeholder
        placeholder.parentNode.replaceChild(weatherWidget, placeholder);
        
        // Fetch the weather data
        fetchWeatherData(weatherWidget, location, units, format);
      } else {
        // No API key, show error state
        const weatherWidget = document.createElement('span');
        weatherWidget.className = 'weather-widget error';
        weatherWidget.textContent = 'Weather API key missing';
        
        // Replace the placeholder
        placeholder.parentNode.replaceChild(weatherWidget, placeholder);
      }
    });
  };

  // Process hex color values in data-color attributes
  useEffect(() => {
    // Only run this after content is rendered
    if (!primaryRef.current) return;
    
    // Find all color-text spans with hex values
    const colorElements = primaryRef.current.querySelectorAll('.color-text[data-color]');
    
    colorElements.forEach(el => {
      const colorValue = el.getAttribute('data-color');
      // Check if it's a hex color (starting with # and followed by hex digits)
      if (colorValue && colorValue.match(/^#[0-9A-Fa-f]{3,8}$/)) {
        // Apply the color directly
        el.style.color = colorValue;
      }
    });
  }, [renderedContent]);

  // Replace placeholder elements with actual React components after the initial render
  useEffect(() => {
    if (!primaryRef.current || !secondaryRef.current) return;
    
    // Initialize both primary and secondary instances
    initializeWidgets(primaryRef.current);
    initializeWidgets(secondaryRef.current);
  }, [renderedContent, weatherApiKey]);

  return (
    <div 
      className="ticker-container"
      style={{
        backgroundColor,
        color: textColor,
        height: `${70 * scale}px`,
        '--content-scale': scale,
        '--font-family': `'${fontFamily}', sans-serif`
      }}
    >
      {debugMode && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '2px 5px',
          fontSize: '10px',
          zIndex: 10000
        }}>
          Debug: {Math.round(primaryRef.current?.offsetWidth || 0)}px
        </div>
      )}
      <div
        className="ticker-track"
        ref={tickerRef}
      >
        <div
          className="ticker-item"
          ref={primaryRef}
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />
        <div
          className="ticker-item"
          ref={secondaryRef}
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />
      </div>
    </div>
  );
};

export default ScrollingText; 